import { ArrowDown2, ArrowUp2 } from 'iconsax-reactjs';
import { WidgetCard } from '@/components/WidgetCard';
import { timeframeShort } from '@pipyar/core';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { VolumeIcon } from '@/components/VolumeIcon';
import { useVolumeSummary } from './api';

/** Timeframes worth a row on the dashboard, coarsest first. */
const SHOWN = ['D1', 'H4', 'H1', 'M30', 'M15'];

/**
 * The nearest level worth watching, per timeframe.
 *
 * Sorted by how close price is to it rather than by timeframe, because the
 * question the tile answers is "am I approaching something" — and the answer is
 * the same whichever chart it came from.
 */
export function VolumeWidget() {
  const { symbol, isLoading: symbolLoading } = useSymbol();
  const query = useVolumeSummary(symbol);
  const { t } = useI18n();

  const digits = query.data?.digits ?? 2;
  const rows = (query.data?.timeframes ?? [])
    .filter((r) => SHOWN.includes(r.timeframe) && r.best !== null)
    .sort((a, b) => Math.abs(a.best!.distance) - Math.abs(b.best!.distance));

  const loading = query.isPending || symbolLoading;

  return (
    <WidgetCard
      to="/volume"
      icon={<VolumeIcon size={16} />}
      title={t('volume.title')}
      module="volume"
    >

      <div className="p-3">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-9 w-full animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">{t('volume.noLevels')}</p>
        ) : (
          <ul className="space-y-1">
            {rows.slice(0, 12).map((r, i) => {
              const best = r.best!;
              const up = best.side === 'above';
              return (
                <li
                  key={r.timeframe}
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg px-2 py-1.5',
                    i === 0 ? 'bg-primary/8' : 'bg-elevated/40',
                  )}
                >
                  <span className="w-9 shrink-0 text-[10px] font-semibold text-muted-foreground">
                    {timeframeShort(r.timeframe)}
                  </span>

                  <span
                    className={cn(
                      'flex size-5 shrink-0 items-center justify-center rounded',
                      up ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
                    )}
                  >
                    {up ? <ArrowUp2 size={12} variant="Bold" /> : <ArrowDown2 size={12} variant="Bold" />}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="tabular block truncate text-xs font-medium">
                      {best.price.toFixed(digits)}
                    </span>
                    <span className="block truncate text-[10px] text-muted-foreground">
                      {t(`volume.${best.kind}` as never)}
                    </span>
                  </span>

                  <span className="tabular shrink-0 text-[10px] text-muted-foreground">
                    {best.distance >= 0 ? '+' : ''}
                    {best.distance.toFixed(digits)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </WidgetCard>
  );
}
